import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';

import useTheme from './hooks/useTheme';
import useBranding from './hooks/useBranding';
import { getStoredAuth, setStoredAuth } from './auth';

import AppLayout from './components/layout/AppLayout';
import TopRightControls from './components/layout/TopRightControls';

// Lazy load the remote components
const BpmUi = React.lazy(() => import('bpm_ui/App'));
const BpmMonitor = React.lazy(() => import('bpm_monitor/App'));
const BpmDesigner = React.lazy(() => import('bpm_designer/App'));
// const BpmJudicial = React.lazy(() => import('bpm_judicial/App'));
// const BpmLaboral = React.lazy(() => import('bpm_laboral/App'));

function getInitialLang() {
  const raw = localStorage.getItem('flowra_lang');
  return raw || 'es';
}

function getActiveModule(pathname) {
  if (pathname.startsWith('/laboral')) return 'laboral';
  if (pathname.startsWith('/judicial')) return 'judicial';
  if (pathname.startsWith('/monitor')) return 'monitor';
  if (pathname.startsWith('/designer')) return 'designer';
  return 'ui';
}

function toStandaloneUrl(pathname, search, hash) {
  const mod = getActiveModule(pathname);
  const base = mod === 'laboral'
    ? 'http://localhost:3005'
    : mod === 'monitor'
      ? '/monitor'
      : mod === 'designer'
        ? '/designer'
        : mod === 'judicial'
          ? 'http://localhost:3004'
          : '/ui';

  const stripPrefix = mod === 'laboral' ? '/laboral' : mod === 'monitor' ? '/monitor' : mod === 'designer' ? '/designer' : mod === 'judicial' ? '/judicial' : '/ui';
  let rest = pathname.startsWith(stripPrefix) ? pathname.slice(stripPrefix.length) : pathname;
  if (!rest) rest = '/';
  if (!rest.startsWith('/')) rest = `/${rest}`;

  return `${base}${rest}${search || ''}${hash || ''}`;
}

function ShellTabs() {
  const tabs = [
    { to: '/ui', label: 'Portal' },
    { to: '/judicial', label: 'Judicial' },
    { to: '/laboral', label: 'Laboral' },
    { to: '/monitor', label: 'Monitor' },
    { to: '/designer', label: 'Designer' }
  ];

  return (
    <nav aria-label="Módulos" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          style={({ isActive }) => ({
            padding: '8px 10px',
            borderRadius: 12,
            border: '1px solid var(--border)',
            background: isActive ? 'color-mix(in hsl, var(--brand-primary) 10%, var(--surface))' : 'var(--surface)',
            fontWeight: 800,
            textDecoration: 'none'
          })}
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

function ShellTopRight() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const [lang, setLang] = useState(() => getInitialLang());
  const [auth, setAuth] = useState(() => getStoredAuth());

  const location = useLocation();

  useBranding(auth?.tenantId);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type !== 'FLOWRA_AUTH_REQUEST' || !event.source || !auth?.token) {
        return;
      }

      event.source.postMessage({ type: 'FLOWRA_AUTH_SYNC', auth }, '*');
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [auth]);

  const userLabel = auth?.username ? `${auth.username} (${auth.role})` : '';

  const openFullscreen = () => {
    const url = toStandaloneUrl(location.pathname, location.search, location.hash);
    window.open(url, '_blank');
  };

  const loginPath = (() => {
    const activeModule = getActiveModule(location.pathname);
    if (activeModule === 'laboral') return '/laboral/login';
    if (activeModule === 'monitor') return '/monitor/login';
    if (activeModule === 'designer') return '/designer/login';
    if (activeModule === 'judicial') return '/judicial/login';
    return '/ui/login';
  })();

  return (
    <TopRightControls
      onFullscreen={openFullscreen}
      fullscreenLabel="Pantalla completa"
      lang={lang}
      onLangChange={(value) => {
        setLang(value);
        localStorage.setItem('flowra_lang', value);
      }}
      onToggleTheme={toggleTheme}
      theme={theme}
      userLabel={userLabel}
      onLogout={
        auth?.token
          ? () => {
              setAuth(null);
              setStoredAuth(null);
              navigate(loginPath, { replace: true });
            }
          : undefined
      }
    />
  );
}

export default function App() {
  useEffect(() => {
    document.title = 'FlowraBPA';
  }, []);

  const navItems = useMemo(() => [], []);

  return (
    <Router>
      <AppLayout
        areaTitle="Suite"
        areaSubtitle=""
        topCenter={<ShellTabs />}
        navItems={navItems}
        topRight={<ShellTopRight />}
        hideSidebar
        fullBleed
      >
        <Suspense fallback={<div className="fr-card" style={{ padding: 16 }}>Cargando…</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/ui" replace />} />
            <Route path="/ui/*" element={<BpmUi embed basename="/ui" />} />
            <Route path="/monitor/*" element={<BpmMonitor embed basename="/monitor" />} />
            <Route path="/designer/*" element={<BpmDesigner embed basename="/designer" />} />
            <Route path="*" element={<Navigate to="/ui" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </Router>
  );
}